import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import Carte from '../components/Carte';
import Confettis from '../components/Confettis';
import { getUser } from '../auth';

const getMeteo = async (lat, lon, date) => {
  try {
    const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,weathercode&timezone=Europe/Paris&start_date=${date}&end_date=${date}`);
    const data = await res.json();
    if (!data.daily) return null;
    const code = data.daily.weathercode[0];
    const tmax = Math.round(data.daily.temperature_2m_max[0]);
    const tmin = Math.round(data.daily.temperature_2m_min[0]);
    const pluie = data.daily.precipitation_probability_max[0];
    let emoji = '☀️', label = 'Ensoleille';
    if (code <= 1) { emoji = '☀️'; label = 'Ensoleille'; }
    else if (code <= 3) { emoji = '⛅'; label = 'Nuageux'; }
    else if (code <= 48) { emoji = '🌫️'; label = 'Brouillard'; }
    else if (code <= 67) { emoji = '🌧️'; label = 'Pluie'; }
    else if (code <= 77) { emoji = '❄️'; label = 'Neige'; }
    else if (code <= 82) { emoji = '🌦️'; label = 'Averses'; }
    else { emoji = '⛈️'; label = 'Orage'; }
    return { emoji, label, tmax, tmin, pluie };
  } catch (e) { return null; }
};

const getCompteARebours = (date, heure) => {
  if (!date || !heure) return null;
  const dateActivite = new Date(`${date}T${heure}`);
  const diff = dateActivite - new Date();
  if (diff <= 0) return null;
  const jours = Math.floor(diff / (1000 * 60 * 60 * 24));
  const heures = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (jours > 0) return `${jours}j ${heures}h`;
  if (heures > 0) return `${heures}h ${minutes}min`;
  return `${minutes}min`;
};

const MOTS_BLOQUES = [
  /\b0[67]\d{8}\b/g,
  /\b\d{2}[\s.-]\d{2}[\s.-]\d{2}[\s.-]\d{2}[\s.-]\d{2}\b/g,
  /instagram|snapchat|tiktok|whatsapp|telegram|signal/gi,
  /mon.num[eé]ro|mon.t[eé]l|contacte.moi|[eé]cris.moi|dm.moi/gi,
];

const filtrerMessage = (texte) => {
  for (const pattern of MOTS_BLOQUES) {
    if (pattern.test(texte)) {
      pattern.lastIndex = 0;
      return { ok: false, raison: 'Ce message contient des informations personnelles non autorisees sur Wooki' };
    }
    pattern.lastIndex = 0;
  }
  return { ok: true };
};

export default function Detail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('detail');
  const [msgs, setMsgs] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [dejaParis, setDejaParis] = useState(false);
  const [nbParticipants, setNbParticipants] = useState(0);
  const [nbEnfantsTotal, setNbEnfantsTotal] = useState(0);
  const [participants, setParticipants] = useState([]);
  const [similaires, setSimilaires] = useState([]);
  const [confettis, setConfettis] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [desinscription, setDesinscription] = useState(false);
  const [enfants, setEnfants] = useState([]);
  const [enfantsSelectionnes, setEnfantsSelectionnes] = useState([]);
  const [nbEnfantsAmenes, setNbEnfantsAmenes] = useState(1);
  const [showEnfantsModal, setShowEnfantsModal] = useState(false);
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [showInscriptionModal, setShowInscriptionModal] = useState(false);
  const [showPodiumModal, setShowPodiumModal] = useState(false);
  const [podium, setPodium] = useState([null, null, null]);
  const [podiumSaved, setPodiumSaved] = useState(false);
  const [msgSignale, setMsgSignale] = useState(null);
  const [meteo, setMeteo] = useState(null);
  const [compteARebours, setCompteARebours] = useState(null);
  const [erreurMsg, setErreurMsg] = useState(null);
  const user = getUser();
  const messagesEndRef = useRef(null);
  const photoChatRef = useRef();

  useEffect(() => { fetchActivity(); fetchEnfants(); }, [id]);

  useEffect(() => {
    if (view !== 'chat') return;
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [view, id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs]);

  useEffect(() => {
    if (!activity) return;
    setCompteARebours(getCompteARebours(activity.date, activity.heure));
    const interval = setInterval(() => {
      setCompteARebours(getCompteARebours(activity.date, activity.heure));
    }, 60000);
    return () => clearInterval(interval);
  }, [activity]);

  const fetchActivity = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('activites').select('*, users(prenom, avatar_url, is_official)').eq('id', id).single();
    if (!error && data) {
      setActivity(data);
      if (data.podium && data.podium.length > 0) {
        setPodium(data.podium);
        setPodiumSaved(true);
      }
      fetchSimilaires(data.type, data.id);
      if (data.latitude && data.longitude && data.date) {
        const m = await getMeteo(data.latitude, data.longitude, data.date);
        setMeteo(m);
      }
    }
    const { count } = await supabase.from('participations').select('*', { count: 'exact', head: true }).eq('activite_id', id);
    setNbParticipants(count || 0);
    const { data: parts } = await supabase.from('participations').select('*, users(id, prenom, avatar_url)').eq('activite_id', id);
    if (parts) {
      setParticipants(parts);
      setNbEnfantsTotal(parts.reduce((sum, p) => sum + (p.nb_enfants || 1), 0));
    }
    if (user) {
      const { data: p } = await supabase.from('participations').select('*').eq('activite_id', id).eq('user_id', user.id).single();
      if (p) setDejaParis(true);
    }
    setLoading(false);
  };

  const fetchEnfants = async () => {
    if (!user) return;
    const { data } = await supabase.from('enfants').select('*').eq('user_id', user.id);
    if (data) {
      setEnfants(data);
      setEnfantsSelectionnes(data.map(e => e.id));
      setNbEnfantsAmenes(data.length || 1);
    }
  };

  const fetchSimilaires = async (type, currentId) => {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase.from('activites').select('*').eq('type', type).neq('id', currentId).gte('date', today).limit(3);
    if (data) setSimilaires(data);
  };

  const fetchMessages = async () => {
    const { data } = await supabase.from('messages').select('*').eq('activite_id', id).order('created_at', { ascending: true });
    if (data) setMsgs(data);
  };

  const handleParticiper = () => {
    if (nbParticipants >= activity.max_familles) return;
    if (!user) { setShowInscriptionModal(true); return; }
    setShowEnfantsModal(true);
  };

  const devenirOrganisateur = () => {
    if (!user) { setShowInscriptionModal(true); return; }
    navigate(`/organiser/${id}`);
  };

  const confirmerParticipation = async () => {
    setShowEnfantsModal(false);
    const enfantsParticipants = enfants.filter(e => enfantsSelectionnes.includes(e.id));
    const enfantsInfo = enfantsParticipants.map(e => `${e.prenom} (${e.age} ans)`).join(', ');
    const nbEnfants = enfants.length > 0 ? enfantsSelectionnes.length : nbEnfantsAmenes;
    await supabase.from('participations').insert({ activite_id: id, user_id: user.id, nb_enfants: nbEnfants });
    setDejaParis(true);
    setNbParticipants(prev => prev + 1);
    setNbEnfantsTotal(prev => prev + nbEnfants);
    if (activity.user_id && activity.user_id !== user.id) {
      const msgNotif = enfantsInfo
        ? `${user.prenom} rejoint "${activity.titre}" avec ${enfantsInfo}`
        : `${user.prenom} rejoint "${activity.titre}" avec ${nbEnfants} enfant${nbEnfants > 1 ? 's' : ''}`;
      await supabase.from('notifications').insert({ user_id: activity.user_id, type: 'participation', message: msgNotif, activite_id: id, lu: false });
    }
    setConfettis(true);
    setTimeout(() => { setConfettis(false); setView('confirm'); }, 1500);
  };

  const handleDesinscrire = async () => {
    setDesinscription(true);
    const { error } = await supabase.from('participations').delete().eq('activite_id', id).eq('user_id', user.id);
    if (!error) {
      setDejaParis(false);
      setNbParticipants(prev => prev - 1);
      setParticipants(prev => prev.filter(p => p.user_id !== user.id));
    }
    setDesinscription(false);
  };

  const sauvegarderPodium = async () => {
    await supabase.from('activites').update({ podium }).eq('id', id);
    setPodiumSaved(true);
    setShowPodiumModal(false);
    for (const p of participants) {
      if (p.user_id) {
        await supabase.from('notifications').insert({
          user_id: p.user_id,
          type: 'podium',
          message: `Le podium de "${activity.titre}" est disponible !`,
          activite_id: id,
          lu: false,
        });
      }
    }
  };

  const sendMsg = async (texte, imageUrl = null) => {
    if (sending) return;
    if (!texte?.trim() && !imageUrl) return;
    if (!user) { setShowInscriptionModal(true); return; }
    if (texte?.trim()) {
      const filtre = filtrerMessage(texte);
      if (!filtre.ok) {
        setErreurMsg(filtre.raison);
        setTimeout(() => setErreurMsg(null), 5000);
        return;
      }
    }
    setSending(true);
    setErreurMsg(null);
    await supabase.from('messages').insert({
      activite_id: id,
      user_id: user?.id || null,
      texte: texte?.trim() || '',
      pseudo: user?.prenom || 'Anonyme',
      image_url: imageUrl || null,
    });
    setNewMsg('');
    await fetchMessages();
    setSending(false);
  };

  const handlePhotoChat = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const ext = file.name.split('.').pop();
      const path = `chat/${id}/${Date.now()}.${ext}`;
      await supabase.storage.from('photos').upload(path, file, { upsert: true });
      const { data } = supabase.storage.from('photos').getPublicUrl(path);
      await sendMsg('', data.publicUrl);
    } catch (err) { alert('Erreur upload photo'); }
  };

  const signalerMessage = (msg) => { setMsgSignale(msg); setShowSignalModal(true); };

  const confirmerSignalement = async () => {
    if (!msgSignale) return;
    await supabase.from('notifications').insert({
      user_id: '844324a7-263b-45cd-9e15-8978820e1972',
      type: 'signalement',
      message: `Signalement dans "${activity?.titre}" : "${msgSignale.texte}"`,
      activite_id: id, lu: false,
    });
    setShowSignalModal(false);
    setMsgSignale(null);
    alert('Merci ! Ce message a ete signale a l equipe Wooki');
  };

  const supprimerActivite = async () => {
    if (activity.user_id !== user?.id) return;
    await supabase.from('activites').delete().eq('id', id);
    navigate('/feed');
  };

  const reorganiserActivite = () => {
    const params = new URLSearchParams({
      titre: activity.titre,
      type: activity.type,
      lieu: activity.lieu,
      emoji: activity.emoji,
      description: activity.description || '',
      ageMin: activity.age_min?.toString() || '',
      ageMax: activity.age_max?.toString() || '',
      maxFamilles: activity.max_familles?.toString() || '5',
    });
    navigate(`/creer?${params.toString()}`);
  };

  const handleShare = async () => {
    const url = `https://wooki.app/detail/${id}`;
    const text = `${activity.titre} - ${new Date(activity.date).toLocaleDateString('fr-FR')} - Rejoins-nous sur Wooki ! ${url}`;
    try {
      if (navigator.share) await navigator.share({ title: activity.titre, text, url });
      else { await navigator.clipboard.writeText(text); setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }
    } catch (e) {
      try { await navigator.clipboard.writeText(text); setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); } catch (e2) {}
    }
  };

  const handleInvite = async () => {
    const lien = user ? `https://wooki.app/invite/${encodeURIComponent(user.prenom)}` : 'https://wooki.app';
    const text = `Salut ! ${user?.prenom || ''} t invite sur Wooki - Sorties en plein air entre familles. Gratuit ! ${lien}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Rejoins Wooki !', text, url: lien });
      else { await navigator.clipboard.writeText(text); alert('Message copie !'); }
    } catch (e) { try { await navigator.clipboard.writeText(text); alert('Copie !'); } catch (e2) {} }
  };

  const Logo = () => (
    <div onClick={() => navigate('/feed')} style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', cursor: 'pointer' }}>
      <span style={{ color: '#52C289' }}>Woo</span><span style={{ color: '#FF7043' }}>ki</span>
    </div>
  );

  const ModalInscription = () => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: '28px 28px 0 0', padding: '28px 24px 48px', width: '100%', maxWidth: 420, textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>🌿</div>
        <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.4rem', color: '#1C2B1E', marginBottom: 8 }}>Rejoins cette sortie !</div>
        <div style={{ fontSize: '0.88rem', color: '#8A9E8D', lineHeight: 1.7, marginBottom: 20 }}>
          Cree ton compte gratuit en 30 secondes pour participer a <strong style={{ color: '#1C2B1E' }}>{activity?.titre}</strong>
        </div>
        <div style={{ background: '#FAFAF7', borderRadius: 16, padding: '12px 16px', marginBottom: 20, textAlign: 'left' }}>
          {['Gratuit — aucune carte bancaire', 'Securise — parents toujours presents', 'Juste ton prenom et ton numero'].map((b, i) => (
            <div key={i} style={{ fontSize: '0.82rem', color: '#4A6355', padding: '5px 0', borderBottom: i < 2 ? '1px solid #E8EFE9' : 'none' }}>✅ {b}</div>
          ))}
        </div>
        <button onClick={() => { setShowInscriptionModal(false); localStorage.setItem('wooki_redirect_after_inscription', `/detail/${id}`); navigate('/inscription'); }} style={{ width: '100%', padding: '17px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', marginBottom: 10, boxShadow: '0 6px 20px rgba(61,154,106,0.35)' }}>
          Creer mon compte gratuit
        </button>
        <button onClick={() => { setShowInscriptionModal(false); navigate('/connexion'); }} style={{ width: '100%', padding: '13px', background: 'transparent', color: '#3D9A6A', border: '2px solid #C5DFC9', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', marginBottom: 10 }}>
          J ai deja un compte
        </button>
        <button onClick={() => setShowInscriptionModal(false)} style={{ width: '100%', padding: '11px', background: 'transparent', color: '#8A9E8D', border: 'none', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
          Continuer a explorer
        </button>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAF7' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12, animation: 'bounce 1s ease infinite' }}>🌿</div>
        <div style={{ fontFamily: 'Fredoka One, cursive', color: '#3D9A6A' }}>Chargement...</div>
      </div>
      <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}`}</style>
    </div>
  );

  if (!activity) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAF7' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>😕</div>
        <div style={{ fontFamily: 'Fredoka One, cursive', color: '#1C2B1E', marginBottom: 16 }}>Activite introuvable</div>
        <button onClick={() => navigate('/feed')} style={{ padding: '12px 24px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, cursor: 'pointer' }}>Retour</button>
      </div>
    </div>
  );

  const date = new Date(activity.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const complet = nbParticipants >= activity.max_familles;
  const completEnfants = activity.max_enfants && nbEnfantsTotal >= activity.max_enfants;
  const estCreateur = user?.id === activity.user_id;
  const isPassee = new Date(activity.date) < new Date();
  const placesRestantes = activity.max_familles - nbParticipants;
  const isTemplate = activity.is_template;
  const sansOrganisateur = isTemplate && !activity.organisateur_choisi;

  if (view === 'chat') return (
    <div style={{ minHeight: '100vh', background: '#F0F4F0', paddingBottom: 80 }}>
      {showSignalModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 24, padding: '28px 24px', width: '100%', maxWidth: 340, textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🚨</div>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: '#1C2B1E', marginBottom: 8 }}>Signaler ce message ?</div>
            <div style={{ fontSize: '0.82rem', color: '#8A9E8D', marginBottom: 20 }}>L equipe Wooki va examiner ce message.</div>
            <button onClick={confirmerSignalement} style={{ width: '100%', padding: '14px', background: '#FF7043', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 10 }}>Oui, signaler</button>
            <button onClick={() => setShowSignalModal(false)} style={{ width: '100%', padding: '13px', background: 'transparent', color: '#8A9E8D', border: '2px solid #E8EFE9', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>Annuler</button>
          </div>
        </div>
      )}

      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'white', borderBottom: '1px solid #F0F5F1', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 2px 12px rgba(28,43,30,0.06)' }}>
        <button style={{ width: 36, height: 36, borderRadius: '50%', background: '#EDF7F1', color: '#3D9A6A', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1rem' }} onClick={() => setView('detail')}>←</button>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #3D9A6A, #52C289)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>{activity.emoji}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: '#1C2B1E' }}>{activity.titre}</div>
          <div style={{ fontSize: '0.7rem', color: '#8A9E8D' }}>Chat · {nbParticipants} famille{nbParticipants > 1 ? 's' : ''} · {nbEnfantsTotal} enfant{nbEnfantsTotal > 1 ? 's' : ''}</div>
        </div>
        <button style={{ background: '#EDF7F1', border: 'none', borderRadius: 100, padding: '6px 12px', color: '#3D9A6A', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }} onClick={handleShare}>↗</button>
      </div>

      <div style={{ background: '#EDF7F1', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #D4EEE0' }}>
        <span>🔒</span>
        <div style={{ fontSize: '0.72rem', color: '#4A6355' }}><strong>Espace securise Wooki</strong> — Pas d echange de coordonnees. Parents presents obligatoires.</div>
      </div>

      <div style={{ padding: '16px 16px 160px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {msgs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#8A9E8D' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>💬</div>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem' }}>Sois le premier a ecrire !</div>
            <div style={{ fontSize: '0.78rem', marginTop: 6, color: '#B0C4B8' }}>Presente-toi et tes enfants 👋</div>
          </div>
        )}
        {msgs.map((m, i) => {
          const isMoi = user && m.user_id === user.id;
          const showName = !isMoi && (i === 0 || msgs[i-1]?.user_id !== m.user_id);
          return (
            <div key={m.id || i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexDirection: isMoi ? 'row-reverse' : 'row', marginBottom: 2 }}>
              {!isMoi && (
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #3D9A6A, #52C289)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', color: 'white', fontWeight: 800, flexShrink: 0 }}>
                  {(m.pseudo || 'A')[0].toUpperCase()}
                </div>
              )}
              <div style={{ maxWidth: '75%' }}>
                {showName && <div style={{ fontSize: '0.68rem', color: '#8A9E8D', fontWeight: 700, marginBottom: 3, marginLeft: 4 }}>{m.pseudo || 'Anonyme'}</div>}
                {m.image_url ? (
                  <img src={m.image_url} alt="photo" style={{ maxWidth: '100%', borderRadius: 16, display: 'block', cursor: 'pointer' }} onClick={() => window.open(m.image_url, '_blank')} />
                ) : (
                  <div style={{ padding: '10px 14px', borderRadius: isMoi ? '18px 18px 4px 18px' : '18px 18px 18px 4px', fontSize: '0.9rem', lineHeight: 1.5, background: isMoi ? 'linear-gradient(135deg, #3D9A6A, #2D7A52)' : 'white', color: isMoi ? 'white' : '#1C2B1E', boxShadow: isMoi ? '0 4px 12px rgba(61,154,106,0.3)' : '0 2px 8px rgba(28,43,30,0.08)' }}>
                    {m.texte}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                  <div style={{ fontSize: '0.62rem', color: '#B0C4B8' }}>{new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                  {!isMoi && <button onClick={() => signalerMessage(m)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.6rem', color: '#C0C0C0', padding: 0 }}>🚩</button>}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ position: 'fixed', bottom: 60, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 420, background: 'white', borderTop: '1px solid #F0F5F1', padding: '10px 16px 16px', zIndex: 200 }}>
        {erreurMsg && (
          <div style={{ background: '#FFF3EE', border: '1px solid #FFD5C8', borderRadius: 12, padding: '8px 12px', marginBottom: 8, fontSize: '0.78rem', color: '#FF7043', lineHeight: 1.4 }}>
            🔒 {erreurMsg}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => photoChatRef.current?.click()} style={{ width: 42, height: 42, borderRadius: '50%', background: '#EDF7F1', border: 'none', cursor: 'pointer', fontSize: '1.2rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📸</button>
          <input ref={photoChatRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChat} />
          <input value={newMsg} onChange={e => { setNewMsg(e.target.value); setErreurMsg(null); }}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMsg(newMsg)}
            placeholder={user ? "Ecrire un message..." : "Inscris-toi pour ecrire..."}
            onClick={() => !user && setShowInscriptionModal(true)}
            readOnly={!user}
            style={{ flex: 1, padding: '12px 18px', border: '2px solid #E8EFE9', borderRadius: 24, fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', outline: 'none', background: user ? '#FAFAF7' : '#F3F4F2', color: '#1C2B1E', cursor: user ? 'text' : 'pointer' }} />
          <button onClick={() => sendMsg(newMsg)} disabled={sending || !newMsg.trim() || !user} style={{ width: 46, height: 46, borderRadius: '50%', background: sending || !newMsg.trim() || !user ? '#E8EFE9' : 'linear-gradient(135deg, #3D9A6A, #2D7A52)', color: 'white', border: 'none', fontSize: '1.1rem', cursor: sending || !newMsg.trim() || !user ? 'not-allowed' : 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {sending ? '⏳' : '➤'}
          </button>
        </div>
      </div>
    </div>
  );

  if (view === 'confirm') return (
    <div style={{ minHeight: '100vh', background: '#FAFAF7', padding: '40px 24px 100px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Confettis trigger={true} onDone={() => {}} />
      <Logo />
      <div style={{ fontSize: '4rem', margin: '20px 0 16px', animation: 'badgePop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}>🎉</div>
      <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.8rem', color: '#1C2B1E', marginBottom: 8 }}>C est confirme !</div>
      <div style={{ fontSize: '0.9rem', color: '#8A9E8D', lineHeight: 1.6, marginBottom: 28 }}>Tu es inscrit a {activity.titre} !</div>
      <div style={{ background: 'white', borderRadius: 24, padding: 20, width: '100%', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 20, textAlign: 'left' }}>
        {[
          { icon: activity.emoji, label: 'Activite', val: activity.titre },
          { icon: '🌅', label: 'Quand', val: date + ' · ' + activity.heure?.slice(0,5) },
          { icon: '📍', label: 'Ou', val: activity.lieu },
          { icon: '🧒', label: 'Enfants inscrits', val: `${enfantsSelectionnes.length || nbEnfantsAmenes} enfant${(enfantsSelectionnes.length || nbEnfantsAmenes) > 1 ? 's' : ''}` },
          meteo ? { icon: meteo.emoji, label: 'Meteo prevue', val: `${meteo.label} · ${meteo.tmax}°C` } : null,
          { icon: '👨‍👩‍👧', label: 'Important', val: 'Parents presents obligatoires 🌿' },
        ].filter(Boolean).map((r, i, arr) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid #F3F4F2' : 'none' }}>
            <span style={{ fontSize: '1.2rem', width: 28 }}>{r.icon}</span>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#8A9E8D' }}>{r.label}</div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{r.val}</div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => setView('chat')} style={{ width: '100%', padding: '17px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', marginBottom: 12, boxShadow: '0 6px 20px rgba(61,154,106,0.35)' }}>💬 Rejoindre le chat</button>
      <button onClick={handleShare} style={{ width: '100%', padding: '14px', background: '#EDF7F1', color: '#3D9A6A', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', marginBottom: 12 }}>{shareCopied ? '✅ Copie !' : '↗ Partager cette activite'}</button>
      <button onClick={handleInvite} style={{ width: '100%', padding: '14px', background: '#FFF3EE', color: '#FF7043', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', marginBottom: 12 }}>👋 Inviter des amis sur Wooki</button>
      <button onClick={() => navigate('/feed')} style={{ width: '100%', padding: '13px', background: 'transparent', color: '#8A9E8D', border: '2.5px solid #E8EFE9', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>← Retour aux activites</button>
      <style>{`@keyframes badgePop{0%{transform:scale(0) rotate(-10deg);opacity:0}70%{transform:scale(1.2) rotate(5deg);opacity:1}100%{transform:scale(1) rotate(0);opacity:1}}`}</style>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAF7', paddingBottom: 80 }}>
      <Confettis trigger={confettis} onDone={() => setConfettis(false)} />

      {showInscriptionModal && <ModalInscription />}

      {/* MODAL PODIUM */}
      {showPodiumModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '28px 28px 0 0', padding: '28px 24px 48px', width: '100%', maxWidth: 420 }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🏆</div>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', color: '#1C2B1E', marginBottom: 4 }}>Podium Wooki</div>
              <div style={{ fontSize: '0.82rem', color: '#8A9E8D' }}>Choisis les 3 gagnants parmi les participants</div>
            </div>
            {[0, 1, 2].map(place => (
              <div key={place} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#4A6355', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {['🥇 1ere place', '🥈 2eme place', '🥉 3eme place'][place]}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {participants.map((p, i) => (
                    <div key={i} onClick={() => {
                      const newPodium = [...podium];
                      newPodium[place] = p.users?.prenom || 'Anonyme';
                      setPodium(newPodium);
                    }} style={{
                      padding: '8px 14px', borderRadius: 100, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700,
                      background: podium[place] === (p.users?.prenom || 'Anonyme') ? '#3D9A6A' : '#F3F4F2',
                      color: podium[place] === (p.users?.prenom || 'Anonyme') ? 'white' : '#1C2B1E',
                    }}>
                      {p.users?.prenom || 'Anonyme'}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={sauvegarderPodium} style={{ width: '100%', padding: '16px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', marginTop: 8, boxShadow: '0 6px 20px rgba(61,154,106,0.35)' }}>
              🏆 Publier le podium !
            </button>
            <button onClick={() => setShowPodiumModal(false)} style={{ width: '100%', padding: '13px', background: 'transparent', color: '#8A9E8D', border: 'none', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', marginTop: 8 }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* MODAL ENFANTS */}
      {showEnfantsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '24px 24px 0 0', padding: '24px', width: '100%', maxWidth: 420, paddingBottom: 40 }}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.2rem', color: '#1C2B1E', marginBottom: 6, textAlign: 'center' }}>Qui vient avec toi ? 🧒</div>
            <div style={{ fontSize: '0.85rem', color: '#8A9E8D', textAlign: 'center', marginBottom: 20 }}>Indique combien d enfants tu amenes</div>
            {enfants.length > 0 ? (
              <>
                {enfants.map(e => (
                  <div key={e.id} onClick={() => setEnfantsSelectionnes(prev => prev.includes(e.id) ? prev.filter(x => x !== e.id) : [...prev, e.id])}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: enfantsSelectionnes.includes(e.id) ? '#EDF7F1' : '#FAFAF7', borderRadius: 16, marginBottom: 8, cursor: 'pointer', border: enfantsSelectionnes.includes(e.id) ? '2px solid #3D9A6A' : '2px solid #E8EFE9' }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>🧒</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: '#1C2B1E' }}>{e.prenom}</div>
                      <div style={{ fontSize: '0.75rem', color: '#8A9E8D' }}>{e.age} ans</div>
                    </div>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: enfantsSelectionnes.includes(e.id) ? '#3D9A6A' : '#E8EFE9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.8rem', fontWeight: 800 }}>
                      {enfantsSelectionnes.includes(e.id) ? '✓' : ''}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'center', marginBottom: 16 }}>
                <button onClick={() => setNbEnfantsAmenes(Math.max(1, nbEnfantsAmenes - 1))} style={{ width: 44, height: 44, borderRadius: '50%', background: '#F3F4F2', border: 'none', fontSize: '1.3rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '2.5rem', color: '#3D9A6A', lineHeight: 1 }}>{nbEnfantsAmenes}</div>
                  <div style={{ fontSize: '0.75rem', color: '#8A9E8D' }}>enfant{nbEnfantsAmenes > 1 ? 's' : ''}</div>
                </div>
                <button onClick={() => setNbEnfantsAmenes(Math.min(10, nbEnfantsAmenes + 1))} style={{ width: 44, height: 44, borderRadius: '50%', background: '#3D9A6A', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>＋</button>
              </div>
            )}
            <button onClick={confirmerParticipation} style={{ width: '100%', padding: '17px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', marginTop: 12, boxShadow: '0 6px 20px rgba(61,154,106,0.35)' }}>
              ✅ Confirmer ma participation !
            </button>
            <button onClick={() => setShowEnfantsModal(false)} style={{ width: '100%', padding: '13px', background: 'transparent', color: '#8A9E8D', border: 'none', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', marginTop: 8 }}>Annuler</button>
          </div>
        </div>
      )}

      {/* HERO */}
      <div style={{ height: 220, position: 'relative', overflow: 'hidden', background: activity.photo_url ? 'transparent' : 'linear-gradient(135deg, #2D7A52, #52C289)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {activity.photo_url ? <img src={activity.photo_url} alt={activity.titre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ fontSize: '5rem', opacity: 0.4 }}>{activity.emoji}</div>}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(28,43,30,0.75))' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
          <button style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', cursor: 'pointer', color: 'white' }} onClick={() => navigate('/feed')}>←</button>
          <Logo />
          <button style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', cursor: 'pointer', color: 'white' }} onClick={handleShare}>{shareCopied ? '✅' : '↗'}</button>
        </div>
        <div style={{ position: 'absolute', bottom: 16, left: 20, right: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.4rem', color: 'white' }}>{activity.titre}</div>
            {isTemplate && <span style={{ background: '#FFB300', color: 'white', padding: '3px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>⭐ Wooki</span>}
            {sansOrganisateur && <span style={{ background: '#FF8F00', color: 'white', padding: '3px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>🙋 Cherche organisateur</span>}
            {(complet || completEnfants) && !isPassee && <span style={{ background: '#FF7043', color: 'white', padding: '3px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>Complet</span>}
            {isPassee && <span style={{ background: 'rgba(0,0,0,0.4)', color: 'white', padding: '3px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>Terminee</span>}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)' }}>{activity.type} · {activity.lieu}</div>
        </div>
      </div>

      <div style={{ padding: '20px 24px 40px' }}>

        {/* BADGE SÉCURITÉ */}
        <div style={{ background: '#EDF7F1', borderRadius: 16, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.2rem' }}>🔒</span>
          <div style={{ fontSize: '0.78rem', color: '#4A6355', fontWeight: 600 }}>Activite securisee Wooki — <strong>Parents presents obligatoires</strong></div>
        </div>

        {/* BOUTON ORGANISATEUR */}
        {sansOrganisateur && !estCreateur && !isPassee && (
          <div style={{ background: 'linear-gradient(135deg, #FF8F00, #FFB300)', borderRadius: 24, padding: '20px', marginBottom: 16 }}>
            <div style={{ textAlign: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: '2.2rem', marginBottom: 8 }}>🙋</div>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: 'white', marginBottom: 6 }}>
                Deviens l organisateur !
              </div>
              <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.6, marginBottom: 12 }}>
                Wooki propose l activite a <strong style={{ color: 'white' }}>{activity.lieu}</strong>. Toi tu choisis l adresse exacte, la date et l heure. Wooki s occupe du reste !
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 14 }}>
              {['📍 Tu choisis le lieu exact', '📅 Tu choisis la date', '📋 Regles incluses', '🏆 Podium Wooki'].map((b, i) => (
                <span key={i} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '4px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 700 }}>{b}</span>
              ))}
            </div>
            <button onClick={devenirOrganisateur} style={{ width: '100%', padding: '16px', background: 'white', color: '#FF8F00', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }}>
              🙋 Je prends en charge cette activite !
            </button>
          </div>
        )}

        {/* COMPTE À REBOURS */}
        {compteARebours && !isPassee && !sansOrganisateur && (
          <div style={{ background: 'linear-gradient(135deg, #1C2B1E, #2D7A52)', borderRadius: 18, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: '1.8rem' }}>⏰</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: 'white' }}>Dans {compteARebours}</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                {placesRestantes <= 2 && !complet ? `Plus que ${placesRestantes} place${placesRestantes > 1 ? 's' : ''} !` : `${placesRestantes} place${placesRestantes > 1 ? 's' : ''} disponible${placesRestantes > 1 ? 's' : ''}`}
              </div>
            </div>
            {placesRestantes <= 2 && !complet && <div style={{ background: '#FF7043', borderRadius: 100, padding: '4px 12px', fontSize: '0.72rem', fontWeight: 800, color: 'white' }}>🔥 Urgent</div>}
          </div>
        )}

        {/* INFOS */}
        {!sansOrganisateur && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {[
              { icon: '🌅', val: date },
              { icon: '⏱', val: activity.heure?.slice(0,5) },
              { icon: '📍', val: activity.lieu },
              { icon: '👶', val: `${activity.age_min}–${activity.age_max} ans` },
            ].map((p, i) => (
              <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'white', borderRadius: 100, padding: '7px 13px', fontSize: '0.8rem', fontWeight: 700, color: '#4A6355', boxShadow: '0 2px 8px rgba(28,43,30,0.07)' }}>
                {p.icon} {p.val}
              </div>
            ))}
          </div>
        )}

        {/* INFOS TEMPLATE SANS ORGANISATEUR */}
        {sansOrganisateur && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {[
              { icon: '📍', val: activity.lieu },
              { icon: '👶', val: `${activity.age_min}–${activity.age_max} ans` },
              { icon: '👨‍👩‍👧', val: `${activity.max_familles} familles max` },
            ].map((p, i) => (
              <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'white', borderRadius: 100, padding: '7px 13px', fontSize: '0.8rem', fontWeight: 700, color: '#4A6355', boxShadow: '0 2px 8px rgba(28,43,30,0.07)' }}>
                {p.icon} {p.val}
              </div>
            ))}
          </div>
        )}

        {/* MÉTÉO */}
        {meteo && !sansOrganisateur && (
          <div style={{ background: 'linear-gradient(135deg, #EDF7F1, #D4EEE0)', borderRadius: 18, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: '2.2rem' }}>{meteo.emoji}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: '#1C2B1E' }}>Meteo prevue ce jour-la</div>
              <div style={{ fontSize: '0.82rem', color: '#4A6355' }}>{meteo.label} · {meteo.tmin}°–{meteo.tmax}°C · {meteo.pluie}% de pluie</div>
            </div>
            {meteo.pluie > 50 && <div style={{ fontSize: '0.72rem', background: '#FFF3EE', color: '#FF7043', padding: '4px 10px', borderRadius: 100, fontWeight: 800 }}>⚠️ Pluie possible</div>}
          </div>
        )}

        {/* RÈGLES */}
        {isTemplate && activity.regles && (
          <div style={{ background: 'white', borderRadius: 24, padding: '16px 18px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#1C2B1E', marginBottom: 12 }}>
              📋 Regles officielles Wooki
            </div>
            {activity.regles.split('.').filter(r => r.trim()).map((regle, i, arr) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0', borderBottom: i < arr.length - 1 ? '1px solid #F3F4F2' : 'none' }}>
                <span style={{ color: '#3D9A6A', fontWeight: 800, flexShrink: 0 }}>✓</span>
                <span style={{ fontSize: '0.82rem', color: '#4A6355', lineHeight: 1.5 }}>{regle.trim()}</span>
              </div>
            ))}
          </div>
        )}

        {/* PODIUM AFFICHÉ */}
        {isPassee && podiumSaved && podium.some(p => p) && (
          <div style={{ background: 'linear-gradient(135deg, #1C2B1E, #2D7A52)', borderRadius: 24, padding: '20px', marginBottom: 16 }}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: 'white', marginBottom: 16, textAlign: 'center' }}>🏆 Podium Wooki</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'flex-end' }}>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '2rem', marginBottom: 4 }}>🥈</div>
                <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 16, padding: '12px 8px', minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.88rem', color: 'white' }}>{podium[1] || '—'}</div>
                </div>
              </div>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 4 }}>🥇</div>
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 16, padding: '16px 8px', minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid rgba(255,179,0,0.5)' }}>
                  <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#FFB300' }}>{podium[0] || '—'}</div>
                </div>
              </div>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '1.8rem', marginBottom: 4 }}>🥉</div>
                <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 16, padding: '10px 8px', minHeight: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.82rem', color: 'white' }}>{podium[2] || '—'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PARTICIPANTS */}
        {!sansOrganisateur && (
          <div style={{ background: 'white', borderRadius: 24, padding: '16px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: participants.length > 0 ? 14 : 0 }}>
              <div style={{ flex: 1, background: complet ? '#FFF3EE' : '#EDF7F1', borderRadius: 14, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', color: complet ? '#FF7043' : '#3D9A6A' }}>{nbParticipants}/{activity.max_familles}</div>
                <div style={{ fontSize: '0.7rem', color: '#8A9E8D', fontWeight: 700 }}>familles</div>
              </div>
              {activity.max_enfants && (
                <div style={{ flex: 1, background: completEnfants ? '#FFF3EE' : '#EDF7F1', borderRadius: 14, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', color: '#FF7043' }}>{nbEnfantsTotal}/{activity.max_enfants}</div>
                  <div style={{ fontSize: '0.7rem', color: '#8A9E8D', fontWeight: 700 }}>enfants</div>
                </div>
              )}
            </div>
            {participants.length > 0 && (
              <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#8A9E8D', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Familles inscrites</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {participants.map((p, i) => (
                    <div key={i} onClick={() => p.users?.prenom && navigate(`/famille/${p.users.prenom}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FAFAF7', borderRadius: 100, padding: '6px 12px 6px 6px', cursor: 'pointer' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', background: 'linear-gradient(135deg, #3D9A6A, #52C289)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {p.users?.avatar_url ? <img src={p.users.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '0.8rem', color: 'white', fontWeight: 800 }}>{(p.users?.prenom || '?')[0].toUpperCase()}</span>}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1C2B1E' }}>{p.users?.prenom || 'Anonyme'}</div>
                        {p.nb_enfants > 0 && <div style={{ fontSize: '0.65rem', color: '#8A9E8D' }}>{p.nb_enfants} enfant{p.nb_enfants > 1 ? 's' : ''}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* BOUTON PARTICIPER */}
        {!dejaParis && !isPassee && !sansOrganisateur && (
          <button onClick={handleParticiper} disabled={complet || completEnfants} style={{ width: '100%', padding: '17px', background: (complet || completEnfants) ? '#F3F4F2' : '#3D9A6A', color: (complet || completEnfants) ? '#8A9E8D' : 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '1rem', cursor: (complet || completEnfants) ? 'not-allowed' : 'pointer', boxShadow: (complet || completEnfants) ? 'none' : '0 6px 20px rgba(61,154,106,0.35)', marginBottom: 16 }}>
            {(complet || completEnfants) ? '🔴 Activite complete' : user ? '✅ Je participe !' : '🌿 Je veux participer !'}
          </button>
        )}

        {/* DÉJÀ INSCRIT */}
        {dejaParis && (
          <div style={{ background: '#EDF7F1', borderRadius: 20, padding: '16px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: '1.2rem' }}>✅</span>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: '#3D9A6A' }}>Tu participes a cette activite !</div>
            </div>
            <button onClick={() => setView('chat')} style={{ width: '100%', padding: '14px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 8 }}>
              💬 Rejoindre le chat
            </button>
            {!estCreateur && (
              <button onClick={handleDesinscrire} disabled={desinscription} style={{ width: '100%', padding: '11px', background: 'transparent', color: '#FF7043', border: '1.5px solid #FFD5C8', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', opacity: desinscription ? 0.6 : 1 }}>
                {desinscription ? 'Desinscription...' : '↩ Se desinscrire'}
              </button>
            )}
          </div>
        )}

        {isPassee && !dejaParis && !estCreateur && !sansOrganisateur && (
          <div style={{ background: '#F3F4F2', borderRadius: 16, padding: '14px 16px', marginBottom: 16, textAlign: 'center' }}>
            <div style={{ fontSize: '0.88rem', color: '#8A9E8D' }}>Cette activite est terminee 🌿</div>
          </div>
        )}

        {/* ORGANISATEUR */}
        {!sansOrganisateur && (
          <div style={{ background: 'white', borderRadius: 24, padding: '16px 18px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#1C2B1E', marginBottom: 10 }}>Organise par</div>
            <div onClick={() => navigate(`/famille/${activity.users?.prenom}`)} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {activity.users?.avatar_url ? <img src={activity.users.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '1.3rem' }}>👤</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: '#3D9A6A' }}>
                  {activity.users?.is_official ? '⭐ Wooki' : activity.users?.prenom || 'Anonyme'}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#8A9E8D' }}>
                  {activity.users?.is_official ? 'Activite officielle Wooki' : 'Voir le profil →'}
                </div>
              </div>
            </div>
          </div>
        )}

        {activity.description && (
          <div style={{ background: 'white', borderRadius: 24, padding: '16px 18px', boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#1C2B1E', marginBottom: 10 }}>A propos</div>
            <div style={{ fontSize: '0.88rem', color: '#4A6355', lineHeight: 1.6 }}>{activity.description}</div>
          </div>
        )}

        {!sansOrganisateur && activity.latitude && activity.longitude && (
          <div style={{ marginBottom: 16 }}>
            <Carte lat={activity.latitude} lon={activity.longitude} lieu={activity.lieu} />
          </div>
        )}

        <div style={{ background: 'white', borderRadius: 24, padding: 16, boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
          <div style={{ fontSize: '0.78rem', color: '#8A9E8D', marginBottom: 12, fontWeight: 700 }}>Partage cette activite !</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleShare} style={{ flex: 1, padding: '14px', background: '#EDF7F1', color: '#3D9A6A', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
              {shareCopied ? '✅ Copie !' : '↗ Partager'}
            </button>
            <button onClick={handleInvite} style={{ flex: 1, padding: '14px', background: '#FFF3EE', color: '#FF7043', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
              👋 Inviter
            </button>
          </div>
        </div>

        {/* BOUTONS CRÉATEUR */}
        {estCreateur && (
          <div style={{ background: 'white', borderRadius: 24, padding: 16, boxShadow: '0 4px 20px rgba(28,43,30,0.07)', marginBottom: 16 }}>
            <div style={{ fontSize: '0.78rem', color: '#8A9E8D', marginBottom: 12, fontWeight: 700 }}>Tu as cree cette activite</div>
            {!isPassee && (
              <button onClick={() => navigate(`/modifier/${id}`)} style={{ width: '100%', padding: '12px', background: '#EDF7F1', color: '#3D9A6A', border: '2px solid #C5DFC9', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', marginBottom: 8 }}>
                ✏️ Modifier cette activite
              </button>
            )}
            {isPassee && (
              <>
                <button onClick={reorganiserActivite} style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #EDF7F1, #D4EEE0)', color: '#3D9A6A', border: '2px solid #C5DFC9', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer', marginBottom: 8 }}>
                  🔄 Reorganiser cette sortie !
                </button>
                <button onClick={() => setShowPodiumModal(true)} style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #FFFDE7, #FFF9C4)', color: '#FF8F00', border: '2px solid #FFE082', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer', marginBottom: 8 }}>
                  🏆 {podiumSaved ? 'Modifier le podium' : 'Publier le podium !'}
                </button>
              </>
            )}
            {!isPassee && (
              <button onClick={supprimerActivite} style={{ width: '100%', padding: '12px', background: '#FFF3EE', color: '#FF7043', border: '2px solid #FFD5C8', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
                🗑️ Annuler cette activite
              </button>
            )}
          </div>
        )}

        {similaires.length > 0 && (
          <div>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#1C2B1E', marginBottom: 12 }}>🌿 Autres activites {activity.type}</div>
            {similaires.map(s => (
              <div key={s.id} onClick={() => { navigate(`/detail/${s.id}`); window.scrollTo(0, 0); }}
                style={{ background: 'white', borderRadius: 16, padding: '12px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 2px 10px rgba(28,43,30,0.06)', cursor: 'pointer' }}>
                <div style={{ width: 44, height: 44, borderRadius: 14, background: '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>{s.emoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.9rem', color: '#1C2B1E' }}>{s.titre}</div>
                  <div style={{ fontSize: '0.72rem', color: '#8A9E8D' }}>{new Date(s.date).toLocaleDateString('fr-FR')} · {s.lieu?.split(',')[0]}</div>
                </div>
                <div style={{ color: '#3D9A6A', fontSize: '0.8rem', fontWeight: 700 }}>→</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}